import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Tooltip,
  IconButton,
  TableSortLabel,
  Box,
  TextField,
  InputAdornment,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Info as InfoIcon, Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { AuditLogEntry } from '../../../core/types';
import {
  formatEntityName as formatEntityNameUtil,
  getActionVerb,
} from '../utils/auditUtility';
import PaginationControls from '../../../shared/components/PaginationControls';
import AuditFilters from './AuditFilters';
import { AuditLogQuery } from '../../../core/types';

interface AuditResultsProps {
  items: AuditLogEntry[];
  loading: boolean;
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (limit: number) => void;
  onRowClick: (row: AuditLogEntry) => void;
  onFiltersChange: (filters: Partial<AuditLogQuery>) => void;
  onSearch: () => void;
  onExport: () => void;
  onClear: () => void;
  settingsAuditMode: boolean;
}

const AuditResults: React.FC<AuditResultsProps> = ({
  items,
  loading,
  page,
  totalPages,
  total,
  limit,
  onPageChange,
  onItemsPerPageChange,
  onRowClick,
  onFiltersChange,
  onSearch,
  onExport,
  onClear,
  settingsAuditMode,
}) => {
  const theme = useTheme();
  const [sortBy, setSortBy] = React.useState<null | 'timestamp' | 'entityName' | 'userName'>(null);
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');
  const [gridSearch, setGridSearch] = React.useState('');

  const handleSort = (column: 'timestamp' | 'entityName' | 'userName') => {
    if (sortBy === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const getSortLabelSx = (active: boolean) => {
    const activeColor = theme.palette.common.white;
    const inactiveColor = alpha(theme.palette.common.white, 0.8);
    const color = active ? activeColor : inactiveColor;
    return {
      color,
      '& .MuiTableSortLabel-icon': {
        color,
        opacity: 1,
      },
      '&:hover': {
        color: activeColor,
        '& .MuiTableSortLabel-icon': { color: activeColor },
      },
      '&.Mui-active': {
        color: activeColor,
        '& .MuiTableSortLabel-icon': { color: activeColor },
      },
    } as const;
  };

  const formatTimestamp = (value?: string): string => {
    if (!value) return '';
    // Example input: '2025-09-24 11:23:06.8009538 +05:00'
    // 1) Insert 'T' between date and time
    // 2) Remove space before timezone offset
    // 3) Trim fractional seconds to milliseconds (3 digits)
    const step1 = value.replace(' ', 'T');
    const step2 = step1.replace(/ ([-+]\d{2}:\d{2})$/, '$1');
    const step3 = step2.replace(/\.(\d{3})\d+/, '.$1');
    const d = new Date(step3);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const renderActionChip = (action: string) => {
    const normalized = (action || '').toLowerCase();
    const color: 'success' | 'error' | 'warning' | 'default' =
      normalized === 'insert'
        ? 'success'
        : normalized === 'update'
        ? 'warning'
        : normalized === 'delete'
        ? 'error'
        : 'default';
    const label = normalized
      ? normalized.charAt(0).toUpperCase() + normalized.slice(1)
      : '';
    return (
      <Chip
        sx={{
          borderRadius: '5px',
          width: '80px',
          color: 'white',
          fontSize: '15px',
        }}
        label={label}
        color={color}
        size="small"
        variant="filled"
      />
    );
  };

  const formatEntityName = (value?: string): string =>
    formatEntityNameUtil(value);

  const buildDescription = (row: AuditLogEntry): string => {
    const rawAction = (row.action as any)?.toString();
    const entityRaw = String(row.entityName || '');
    const actionVerb = getActionVerb(rawAction) || '';
    const friendlyEntity = formatEntityName(entityRaw);
    if (actionVerb && friendlyEntity)
      return `${actionVerb.charAt(0).toUpperCase()}${actionVerb.slice(
        1
      )} ${friendlyEntity}`;
    if (friendlyEntity) return friendlyEntity;
    return actionVerb;
  };

  const filteredAndSortedItems = React.useMemo(() => {
    // First, filter by grid search
    let result = items;
    if (gridSearch.trim()) {
      const searchLower = gridSearch.toLowerCase().trim();
      result = items.filter(row => {
        const recordNo = (row.recordNo ?? '').toLowerCase();
        const userName = (row.userName ?? '').toLowerCase();
        const entityName = (row.entityName ?? '').toLowerCase();
        const action = (row.action ?? '').toLowerCase();
        const timestamp = ((row as any).timestamp ?? (row as any).timeStamp ?? '').toLowerCase();
        const description = buildDescription(row).toLowerCase();
        return (
          recordNo.includes(searchLower) ||
          userName.includes(searchLower) ||
          entityName.includes(searchLower) ||
          action.includes(searchLower) ||
          timestamp.includes(searchLower) ||
          description.includes(searchLower)
        );
      });
    }

    // Then, sort if needed
    if (!sortBy) return result;
    const copy = [...result];
    const dir = sortDirection === 'asc' ? 1 : -1;
    copy.sort((a, b) => {
      if (sortBy === 'timestamp') {
        const av = ((a as any).timestamp ?? (a as any).timeStamp ?? '') as string;
        const bv = ((b as any).timestamp ?? (b as any).timeStamp ?? '') as string;
        const norm = (v: string) => {
          if (!v) return 0;
          const step1 = v.replace(' ', 'T');
          const step2 = step1.replace(/ ([-+]\d{2}:\d{2})$/, '$1');
          const step3 = step2.replace(/\.(\d{3})\d+/, '.$1');
          const d = new Date(step3);
          return isNaN(d.getTime()) ? 0 : d.getTime();
        };
        const an = norm(av);
        const bn = norm(bv);
        if (an === bn) return 0;
        return an > bn ? dir : -dir;
      }
      if (sortBy === 'entityName') {
        const an = (a.entityName ?? '').toString().toLowerCase();
        const bn = (b.entityName ?? '').toString().toLowerCase();
        return an.localeCompare(bn) * dir;
      }
      const an = (a.userName ?? '').toString().toLowerCase();
      const bn = (b.userName ?? '').toString().toLowerCase();
      return an.localeCompare(bn) * dir;
    });
    return copy;
  }, [items, sortBy, sortDirection, gridSearch]);

  return (
    <Paper>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 1.25,
          flexWrap: 'wrap',
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.6)
              : alpha(theme.palette.grey[50], 0.8),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            minWidth: 0,
            flex: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 500,
            }}
          >
            
          </Typography>

          <AuditFilters
            variant="inline"
            onFiltersChange={onFiltersChange}
            onSearch={onSearch}
            onExport={onExport}
            onClear={onClear}
            loading={loading}
            settingsAuditMode={settingsAuditMode}
          />
        </Box>
      </Box>
      <PaginationControls
        currentPage={page}
        totalPages={totalPages}
        totalItems={total}
        itemsPerPage={limit}
        onPageChange={onPageChange}
        onItemsPerPageChange={v => {
          onItemsPerPageChange(v);
        }}
        pageSizeOptions={[25, 50, 100, 200]}
        beforePageNumbers={
          <TextField
            size="small"
            placeholder="Filter results..."
            value={gridSearch}
            onChange={e => setGridSearch(e.target.value)}
            sx={{
              width: { xs: '100%', sm: 220 },
              '& .MuiOutlinedInput-root': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.common.white, 0.05)
                    : theme.palette.common.white,
                borderRadius: 2,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.08)
                      : theme.palette.common.white,
                },
                '&.Mui-focused': {
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.1)
                      : theme.palette.common.white,
                  boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: theme.palette.text.secondary }}
                  />
                </InputAdornment>
              ),
              endAdornment: gridSearch ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setGridSearch('')}
                    sx={{ p: 0.5 }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        }
      />
      <TableContainer sx={{ height: 600, overflow: 'auto' }}>
        <Table
          stickyHeader
          sx={{
            '& th': { fontWeight: 700, fontSize: 'calc(0.875rem + 2px)' },
            '& td': { fontSize: 'calc(0.875rem + 2px)' },
            '& tbody tr:nth-of-type(odd)': {
              backgroundColor: 'action.hover',
            },
            '& td, & th': { borderBottomColor: 'divider' },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 150 }}>Record No</TableCell>
              <TableCell
                sx={{
                  minWidth: 200,
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                }}
              >
                <TableSortLabel
                  active={sortBy === 'timestamp'}
                  direction={sortBy === 'timestamp' ? sortDirection : 'asc'}
                  onClick={() => handleSort('timestamp')}
                  sx={getSortLabelSx(sortBy === 'timestamp')}
                >
                  Timestamp
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minWidth: 200 }}>
                <TableSortLabel
                  active={sortBy === 'userName'}
                  direction={sortBy === 'userName' ? sortDirection : 'asc'}
                  onClick={() => handleSort('userName')}
                  sx={getSortLabelSx(sortBy === 'userName')}
                >
                  User
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minWidth: 200 }}>
                <TableSortLabel
                  active={sortBy === 'entityName'}
                  direction={sortBy === 'entityName' ? sortDirection : 'asc'}
                  onClick={() => handleSort('entityName')}
                  sx={getSortLabelSx(sortBy === 'entityName')}
                >
                  Module
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minWidth: 200 }}>Action</TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredAndSortedItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {items.length === 0
                    ? 'No audit records found'
                    : 'No records match your filter'}
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedItems.map(row => (
                <TableRow key={`${row.auditIndexId}`} hover>
                  <TableCell
                    dangerouslySetInnerHTML={{
                      __html: row.recordNo || '-',
                    }}
                  />
                  <TableCell>
                    {formatTimestamp(
                      (row as any).timestamp ?? (row as any).timeStamp
                    )}
                  </TableCell>
                  <TableCell>{row.userName}</TableCell>
                  <TableCell>
                    {formatEntityName(row.entityName as any)}
                  </TableCell>
                  <TableCell>
                    {renderActionChip(row.action as string)}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <span>{buildDescription(row as any)}</span>
                    {row.changeJson ? (
                      <Tooltip title="Click to view change">
                        <IconButton
                          size="small"
                          onClick={() => onRowClick(row as any)}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>


    </Paper>
  );
};

export default AuditResults;
